import {
  TimingMeasurementType,
  TimingRelationship,
} from '@/types/api';
import type {
  TimingConfig,
  LandmarkEventConfig,
} from '@/types/api';
import { getLandmarkLabel } from '@/utils/landmarkDetection';

// Split out of TimingGoalAnnotation.tsx (react-refresh/only-export-components)
// — `generateSentence` is exported for direct unit testing; `resolveEventConfig`
// moved alongside it since it's a dependency also used by the component
// itself (re-imported below in TimingGoalAnnotation.tsx). Behavior unchanged.

/** Resolve effective event config: prefer event_a/event_b, fallback to legacy */
export function resolveEventConfig(
  config: TimingConfig,
  landmark: 'a' | 'b'
): LandmarkEventConfig {
  const eventField = landmark === 'a' ? config.event_a : config.event_b;
  if (eventField) return eventField;
  // Legacy fallback: use shared measurement_type
  return {
    measurement_type: config.measurement_type,
    velocity_threshold: config.velocity_threshold,
  };
}

/**
 * Generate a plain-language summary sentence for a timing goal config.
 *
 * Maps each measurement type to a human verb and each relationship to a
 * connective so coaches can read at a glance what the goal checks.
 * Returns "" when either landmark is unset (incomplete config).
 *
 * Exported for direct unit testing.
 */
export function generateSentence(config: TimingConfig): string {
  if (!config.landmark_a || !config.landmark_b) return '';

  const aLabel = getLandmarkLabel(config.landmark_a);
  const bLabel = getLandmarkLabel(config.landmark_b);

  const eventA = resolveEventConfig(config, 'a');
  const eventB = resolveEventConfig(config, 'b');

  const verbMap: Record<TimingMeasurementType, string> = {
    [TimingMeasurementType.VELOCITY_ONSET]: 'starts moving',
    [TimingMeasurementType.PEAK_VELOCITY]: 'reaches peak velocity',
    [TimingMeasurementType.GROUND_CONTACT]: 'contacts the ground',
    [TimingMeasurementType.PEAK_EXTENSION]: 'reaches full extension',
    [TimingMeasurementType.ANGLE_THRESHOLD]: 'hits the angle threshold',
    [TimingMeasurementType.OFF_THE_GROUND]: 'leaves the ground',
  };

  const verbA = verbMap[eventA.measurement_type] ?? 'moves';
  const verbB = verbMap[eventB.measurement_type] ?? 'moves';

  let connective: string;
  if (config.relationship === TimingRelationship.SEQUENTIAL_A_FIRST) {
    connective = 'BEFORE';
  } else if (config.relationship === TimingRelationship.SEQUENTIAL_B_FIRST) {
    connective = 'AFTER';
  } else {
    connective = 'AT THE SAME TIME AS';
  }

  return `${aLabel} ${verbA} ${connective} ${bLabel} ${verbB}`;
}
